export interface YearMonth {
  year: number;
  month: number;
}

export function toYearMonth(date: Date): YearMonth {
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
}

export function parseYearMonth(
  isoDate: string | null | undefined,
): YearMonth | null {
  const match = /^(\d{4})-(\d{2})/.exec(isoDate ?? '');
  return match ? { year: Number(match[1]), month: Number(match[2]) } : null;
}

function monthIndex(value: YearMonth): number {
  return value.year * 12 + value.month;
}

/** Months of a project, both ends counted; a missing end counts as today, a start in the future as 0. */
export function projectMonths(
  beginn: string | null,
  ende: string | null,
  today: YearMonth,
): number {
  const start = parseYearMonth(beginn);
  if (!start) {
    return 0;
  }
  const end = parseYearMonth(ende) ?? today;
  return Math.max(0, monthIndex(end) - monthIndex(start) + 1);
}

/** Length of the union of all project periods: overlapping or adjoining periods are merged and counted once. */
export function totalExperienceMonths(
  periods: { beginn: string | null; ende: string | null }[],
  today: YearMonth,
): number {
  const intervals = periods
    .map((period) => {
      const start = parseYearMonth(period.beginn);
      const end = parseYearMonth(period.ende) ?? today;
      return start ? [monthIndex(start), monthIndex(end)] : null;
    })
    .filter(
      (interval): interval is number[] =>
        !!interval && interval[1] >= interval[0],
    )
    .sort((a, b) => a[0] - b[0]);

  let total = 0;
  let current: number[] | null = null;
  for (const [start, end] of intervals) {
    if (!current || start > current[1] + 1) {
      if (current) {
        total += current[1] - current[0] + 1;
      }
      current = [start, end];
    } else {
      current[1] = Math.max(current[1], end);
    }
  }
  if (current) {
    total += current[1] - current[0] + 1;
  }
  return total;
}

export function formatYearMonth(isoDate: string | null): string {
  const value = parseYearMonth(isoDate);
  return value ? `${String(value.month).padStart(2, '0')}/${value.year}` : '';
}
